/**
 * Le hero de « Notre IA » — et son bandeau de quatre mesures.
 *
 * CE QUE LE DESIGN A CHANGÉ (13/08/2026)
 * ──────────────────────────────────────
 * Les quatre chiffres flottaient entre deux filets, centrés, sans autre
 * hiérarchie que leur taille. Ils vivent maintenant dans UNE carte
 * blanche à quatre colonnes : chaque cellule s'ouvre sur son rang (01…04)
 * et son domaine, pose son nombre, puis une jauge de 2 px sous la
 * légende. La jauge de la précision s'arrête à 94,7 % — la barre DIT la
 * mesure au lieu de la décorer.
 *
 * LES CHIFFRES SONT VRAIS, OU ILS NE SONT PAS
 * ───────────────────────────────────────────
 * La quatrième cellule annonçait « 2.5 M DOCUMENTS JURIDIQUES ». Le
 * produit annonce 300 000+ (`CORPUS_SOURCES` — lire son commentaire, c'est
 * une décision produit et non le compte de la base). Elle lit désormais
 * cette source unique, avec son suffixe « + ».
 *
 * Son rang s'appelle « SOURCES » et non plus « ENTRAÎNEMENT » : ces
 * documents sont un corpus de RÉCUPÉRATION, pas un jeu d'entraînement.
 * Garder le mot aurait remplacé un chiffre faux par une affirmation
 * fausse.
 *
 * `900 K ARTICLES DE CODE` (cellule 02) est laissé tel quel : ce n'est
 * pas un compte de documents — les textes de loi contiennent bien des
 * centaines de milliers d'articles — mais aucune source du dépôt ne
 * l'étaye. À vérifier avant de le laisser en vitrine.
 */
import React from 'react';
import { corpusCountPlus } from './corpus';

/**
 * `fill` — la largeur de la jauge, en pourcentage. 100 quand la mesure
 * n'est pas un taux ; la valeur elle-même quand elle en est un.
 */
const STATS = [
  { key: 'passes',   kicker: 'pipeline',   value: '7',    unit: '×', label: 'passes_label',   fill: '100%' },
  { key: 'statutes', kicker: 'corpus',     value: '900',  unit: 'K', label: 'statutes_label', fill: '100%' },
  { key: 'accuracy', kicker: 'validation', value: '94.7', unit: '%', label: 'accuracy_label', fill: '94.7%' },
  { key: 'docs',     kicker: 'sources',    value: null,   unit: '',  label: 'docs_label',     fill: '100%' },
];

export default function EngineHero({ t, language }) {
  return (
    <section className="engine-hero archer-hero-section" data-testid="engine-hero">
      {/* Le même halo radial bleuté que le hero d'Archer for Teams :
          ellipse débordante en haut, décorative, rognée par
          l'`overflow:hidden` de `.engine-hero`. Valeurs dans hero.css. */}
      <div aria-hidden="true" className="archer-hero-halo" />
      <div className="engine-hero-inner">
        <div className="engine-eyebrow">{t('hero.eyebrow')}</div>
        <h1 className="engine-h1 archer-hero-title">
          {t('hero.title_before')} <span className="engine-h1-blue archer-hero-accent">{t('hero.title_highlight')}</span>
        </h1>
        <p className="engine-hero-p archer-hero-subtitle">{t('hero.description')}</p>

        <div className="engine-hero-stats" data-testid="engine-hero-stats">
          <div className="engine-hero-stats-grid">
            {STATS.map((s, i) => (
              <div className="engine-hero-stat" key={s.key} data-stat={s.key}>
                <div className="engine-hero-stat-head">
                  <span className="engine-hero-stat-idx">{String(i + 1).padStart(2, '0')}</span>
                  <span className="engine-hero-stat-kicker">{t(`hero.stats.${s.kicker}_kicker`)}</span>
                </div>
                <div className="engine-hero-stat-num">
                  {s.value ?? corpusCountPlus(language)}
                  {s.unit && <span className="unit">{s.unit}</span>}
                </div>
                <div className="engine-hero-stat-label">{t(`hero.stats.${s.label}`)}</div>
                <div className="engine-hero-stat-track">
                  <div className="engine-hero-stat-fill" style={{ width: s.fill }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
